export const APP_ACCESS_KEYS = [
  "financeiro",
  "suprimentos",
  "kanban",
  "orcamentos",
  "gerencial",
  "chaoDeObra",
  "chaoDeObraAprovar",
  "comercial",
  "solicitacaoPagamento",
  "solicitacaoPagamentoAprovar",
  "solicitacaoPagamentoAprovarFinanceira",
  "tarefas",
  "locacoes",
  "manutencao",
  "folhaPagamentos",
  "genteGestao",
  "marketing",
] as const;

export type AppAccessKey = (typeof APP_ACCESS_KEYS)[number];

export type AppAccessMap = Record<AppAccessKey, boolean>;

export const APP_ACCESS_LABELS: Record<AppAccessKey, string> = {
  financeiro: "Financeiro",
  suprimentos: "Suprimentos",
  kanban: "Kanban",
  orcamentos: "Orçamentos",
  gerencial: "Gerencial",
  chaoDeObra: "Chão de Obra",
  chaoDeObraAprovar: "Chão de Obra — aprovar registros de medição",
  comercial: "Comercial",
  solicitacaoPagamento: "Solicitação de Pagamento",
  solicitacaoPagamentoAprovar: "Solicitação de Pagamento — aprovação operacional",
  solicitacaoPagamentoAprovarFinanceira: "Solicitação de Pagamento — aprovação financeira",
  tarefas: "Tarefas",
  locacoes: "Semog Locações",
  manutencao: "Manutenção",
  folhaPagamentos: "Folha de Pagamentos",
  genteGestao: "Gente & Gestão",
  marketing: "Marketing",
};

/**
 * Quem tem aprovação operacional ou financeira recebe acesso base ao módulo
 * (mesma UI: abas, histórico, criação e consulta).
 */
export function finalizeAppAccess(map: AppAccessMap): AppAccessMap {
  const out = { ...map };
  if (out.solicitacaoPagamentoAprovar || out.solicitacaoPagamentoAprovarFinanceira) {
    out.solicitacaoPagamento = true;
  }
  if (out.chaoDeObraAprovar) {
    out.chaoDeObra = true;
  }
  return out;
}

export function canAccessSolicitacaoPagamentoFromMap(access: AppAccessMap): boolean {
  const a = finalizeAppAccess(access);
  return (
    a.solicitacaoPagamento === true ||
    a.solicitacaoPagamentoAprovar === true ||
    a.solicitacaoPagamentoAprovarFinanceira === true
  );
}

/** Visível no menu se pode solicitar ou aprovar pagamentos. */
export function canAccessSolicitacaoPagamentoModule(
  can: (key: AppAccessKey) => boolean,
): boolean {
  return (
    can("solicitacaoPagamento") ||
    can("solicitacaoPagamentoAprovar") ||
    can("solicitacaoPagamentoAprovarFinanceira")
  );
}

/** Módulo Locações — permissão do grupo ou vínculo como operador de contrato ativo. */
export function canAccessLocacoesModule(
  can: (key: AppAccessKey) => boolean,
  locacoesOperador?: boolean,
): boolean {
  return can("locacoes") || locacoesOperador === true;
}

/** App /operador — operador vinculado a contrato ativo (independe da flag locacoes do grupo). */
export function canAccessOperadorApp(locacoesOperador?: boolean): boolean {
  return locacoesOperador === true;
}

/** Usuário só operador: sem permissão locacoes full nem outros módulos (exceto admin). */
export function isOperadorOnlyUser(
  can: (key: AppAccessKey) => boolean,
  opts?: { locacoesOperador?: boolean; role?: string },
): boolean {
  if (!opts?.locacoesOperador) return false;
  if (opts.role === "admin") return false;
  if (can("locacoes")) return false;
  return !APP_ACCESS_KEYS.some((k) => k !== "locacoes" && can(k));
}

/** Usuário só Marketing: acesso exclusivo ao dashboard de mídia (exceto admin). */
export function isMarketingOnlyUser(
  can: (key: AppAccessKey) => boolean,
  opts?: { locacoesOperador?: boolean; role?: string },
): boolean {
  if ((opts?.role ?? "").toLowerCase() === "admin") return false;
  if (opts?.locacoesOperador) return false;
  if (!can("marketing")) return false;
  return !APP_ACCESS_KEYS.some((k) => k !== "marketing" && can(k));
}

/** Destino pós-login conforme perfil. */
export function defaultRouteAfterLogin(
  can: (key: AppAccessKey) => boolean,
  opts?: { locacoesOperador?: boolean; role?: string },
): string {
  if (isOperadorOnlyUser(can, opts)) return "/operador";
  if (isMarketingOnlyUser(can, opts)) return "/marketing";
  return "/";
}

/** Legado: grupos salvos com `chaoDeFabrica` antes da renomeação. */
export function normalizeRawAppAccess(raw: unknown): AppAccessMap {
  const o = emptyAppAccess();
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return o;
  const src = raw as Record<string, unknown>;
  for (const k of APP_ACCESS_KEYS) {
    if (src[k] === true) o[k] = true;
  }
  if (src.chaoDeFabrica === true) o.chaoDeObra = true;
  return finalizeAppAccess(o);
}

export function emptyAppAccess(): AppAccessMap {
  const out = {} as AppAccessMap;
  for (const k of APP_ACCESS_KEYS) out[k] = false;
  return out;
}

/** União (OR) de vários mapas — espelha o backend quando o usuário tem múltiplos grupos. */
export function mergeAppAccess(maps: AppAccessMap[]): AppAccessMap {
  const out = emptyAppAccess();
  for (const m of maps) {
    for (const k of APP_ACCESS_KEYS) {
      if (m[k]) out[k] = true;
    }
  }
  return finalizeAppAccess(out);
}
